import { PxtraApiClientLegacy } from '@pxtra-client-lib-legacy';
import { UserId, LegalDocument, LegalDocumentAcceptance } from '@pxtra-client-lib-legacy/gen';
import { FormikValues } from 'formik';
import { t } from 'i18next';
import { makeAutoObservable, runInAction, when } from 'mobx';
import * as Yup from 'yup';
import { env } from '../lib/env';

const googleOauthUrl = env('GOOGLE_OAUTH_URL');
const googleOauthClientId = env('GOOGLE_OAUTH_CLIENT_ID');
const googleOauthRedirectUri = env('GOOGLE_OAUTH_REDIRECT_URI');

const microsoftOauthUrl = env('MICROSOFT_OAUTH_URL');
const microsoftOauthClientId = env('MICROSOFT_OAUTH_CLIENT_ID');
const microsoftOauthRedirectUri = env('MICROSOFT_OAUTH_REDIRECT_URI');

export type ErrorObject = {
  [field: string]: string[];
};
class AuthenticationStore {
  private apiClient: PxtraApiClientLegacy;
  private _isLoggedIn = false;
  private _currentUser?: string | null;
  private _currentUserId?: UserId;
  private _language?: string | null;

  constructor(apiClient: PxtraApiClientLegacy) {
    this.apiClient = apiClient;
    this.apiClient.isLoggedIn.subscribe((value) => {
      runInAction(() => (this._isLoggedIn = value));
    });
    this.apiClient.currentUser?.subscribe((value) => {
      runInAction(() => (this._currentUser = value));
    });
    this.apiClient.user.userIdRetrieve()?.then((userId) => {
      runInAction(() => {
        this._currentUserId = userId;
      });
    });

    makeAutoObservable(this);
    when(
      () => !this.isLoggedIn,
      () => {
        this._clear();
      }
    );
  }

  private _clear() {
    this._currentUser = undefined;
    this._currentUserId = undefined;
    this._language = undefined;
  }

  public getGoogleLoginUrl() {
    const responseType = 'code';
    const scope = 'openid email';

    const queryString = this.buildOauthQueryString(
      googleOauthClientId,
      responseType,
      scope,
      googleOauthRedirectUri
    );
    return `${googleOauthUrl}?${queryString}`;
  }

  public getMicrosoftLoginUrl() {
    const responseType = 'code';
    const scope = 'openid email';
    const queryString = this.buildOauthQueryString(
      microsoftOauthClientId,
      responseType,
      scope,
      microsoftOauthRedirectUri
    );
    return `${microsoftOauthUrl}?${queryString}`;
  }

  public yupErrorToErrorObject(error: Yup.ValidationError, object: ErrorObject) {
    try {
      error.inner.forEach((x) => {
        if (x.path !== undefined) {
          const list = object[x.path];
          list.push(x.errors[0]);
          object[x.path] = list;
        }
      });
    } catch (e) {
      console.error(e);
    }
    return object;
  }

  public async validatePassword(values: FormikValues) {
    const schema = Yup.object().shape({
      new_password: Yup.string()
        .required(t('form:FIELD_IS_REQUIRED'))
        .min(8, t('account:PASSWORD_LENGTH'))
        .matches(/^(?=.*[0-9])/, t('account:PASSWORD_NUMBER'))
        .matches(/^(?=.*[A-Z])/, t('account:PASSWORD_UPPER_CASE'))
        .matches(/^(?=.*[a-z])/, t('account:PASSWORD_LOWER_CASE'))
        .matches(/^(?=.*[()[\]{}|`~!@#$%^&*_+\-=;:'",<>./?])/, t('account:PASSWORD_SYMBOL')),
      confirm_new_password: Yup.string()
        .required(t('form:FIELD_IS_REQUIRED'))
        .oneOf([Yup.ref('new_password'), null], t('account:PASSWORDS_ARE_NOT_SAME')),
      ...('old_password' in values && {
        old_password: Yup.string().required(t('form:FIELD_IS_REQUIRED'))
      })
    });
    return schema
      .validate(values, { abortEarly: false })
      .then(() => {
        const errors = {
          new_password: [],
          confirm_new_password: [],
          ...('old_password' in values && {
            old_password: []
          })
        };
        return errors;
      })
      .catch((e: Yup.ValidationError) => {
        const errors = this.yupErrorToErrorObject(e, {
          new_password: [],
          confirm_new_password: [],
          ...('old_password' in values && { old_password: [] })
        });
        return errors;
      });
  }

  public async loginFirstStep(username: string, password: string) {
    const { twoFactorNextStep } = await this.apiClient.loginFirstStep(
      username,
      password,
      env('JWT_AUDIENCE')
    );
    return twoFactorNextStep;
  }

  public async loginSecondStep(code: string) {
    await this.apiClient.loginSecondStep(code, env('JWT_AUDIENCE'));
    this.fetchLanguage();
    this.apiClient.user.userIdRetrieve()?.then((userId) => {
      runInAction(() => {
        this._currentUserId = userId;
      });
    });
  }

  public async requestCode(method = 'email') {
    await this.apiClient.requestTwoFactorCode(method);
  }

  public async logout() {
    return this.apiClient.logout();
  }

  public async loginWithGoogle(authorizationCode: string) {
    return this.apiClient.loginWithOAuthCode(
      env('JWT_AUDIENCE'),
      authorizationCode,
      googleOauthClientId,
      googleOauthRedirectUri
    );
  }

  public async loginWithMicrosoft(authorizationCode: string) {
    return this.apiClient.loginWithOAuthCode(
      env('JWT_AUDIENCE'),
      authorizationCode,
      microsoftOauthClientId,
      microsoftOauthRedirectUri
    );
  }

  public async sendResetPasswordMail(email: string) {
    return this.apiClient.auth.authResetPasswordCreate({
      requestBody: {
        email: email,
        cm: true
      }
    });
  }

  public async userTwoFactorMethods() {
    const response = await this.apiClient.auth.authMfaUserActiveMethodsList({});
    return response.results;
  }

  public async enableTwoFactor(method = 'email') {
    await this.apiClient.auth.authActivateCreate({ method });
  }

  public async confirmEnableTwoFactor(code: string, method = 'email') {
    await this.apiClient.auth.authActivateConfirmCreate({ method, requestBody: { code } });
  }

  public async disableTwoFactor(method = 'email') {
    await this.apiClient.auth.authDeactivateCreate({ method, requestBody: {} });
  }

  public async setLanguage(language: string) {
    const res = await this.apiClient.user.userLanguageUpdate({
      requestBody: {
        language
      }
    });
    runInAction(() => (this._language = res.language));
  }

  public async setNewPassword(
    password: string,
    confirmPassword: string,
    passwordResetId: string,
    userId: string,
    language: string
  ) {
    return this.apiClient.auth.authSetNewPasswordCreate({
      requestBody: {
        password,
        confirm_password: confirmPassword,
        password_reset_id: passwordResetId,
        user_id: userId,
        language: language
      }
    });
  }

  public async fetchLanguage() {
    const res = await this.apiClient.user.userLanguageRetrieve();
    runInAction(() => (this._language = res.language));
  }

  public async fetchAgreements(accepted = true, audience = 'Company Manager') {
    let agreements: LegalDocument[] | undefined;
    let acceptances: LegalDocumentAcceptance[] | undefined;

    await Promise.all([
      this.apiClient.user
        .userLegalDocumentsList({ audience: audience })
        .then((agreementsList) => (agreements = agreementsList.results)),
      this.apiClient.user
        .userAcceptancesList({})
        .then((acceptancesList) => (acceptances = acceptancesList.results))
    ]);
    const acceptedAgreements = acceptances?.map((acceptance) => acceptance.legal_document) || [];

    if (agreements && acceptedAgreements) {
      return (
        agreements.filter((agreement) => {
          return (
            (accepted
              ? acceptedAgreements.includes(agreement.id)
              : !acceptedAgreements.includes(agreement.id)) &&
            !agreements?.some(
              (otherAgreement) =>
                otherAgreement.key === agreement.key && otherAgreement.id > agreement.id
            )
          );
        }) || []
      );
    }
    return [];
  }

  public async fetchAGBAgreement(audience = 'Company Manager') {
    let agreements: LegalDocument[] | undefined;
    await this.apiClient.user
      .userLegalDocumentsList({ audience: audience })
      .then((agreementsList) => (agreements = agreementsList.results));

    let AGBAgreements: LegalDocument[] = [];

    if (agreements) {
      AGBAgreements = agreements.filter((agreement) => agreement.key === 'terms');
      AGBAgreements = AGBAgreements.filter((agreement) => {
        return !AGBAgreements.some((otherAgreement) => {
          return otherAgreement.id > agreement.id;
        });
      });
      if (AGBAgreements) return AGBAgreements[0];
    }
    return undefined;
  }

  public async submitAgreement(agreementId: number) {
    await this.apiClient.user.userAcceptancesCreate({
      requestBody: {
        legal_document_id: agreementId
      }
    });
  }

  public get isLoggedIn() {
    return this._isLoggedIn;
  }

  public get currentUser() {
    return this._currentUser;
  }

  public get currentUserId() {
    return this._currentUserId;
  }

  public get language() {
    return this._language;
  }

  private buildOauthQueryString(
    clientId: string,
    responseType: string,
    scope: string,
    redirectUri: string
  ) {
    return `client_id=${clientId}&response_type=${responseType}&scope=${scope}&redirect_uri=${redirectUri}&prompt=select_account`;
  }
}

export default AuthenticationStore;
